import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import brandLogo from "../../assets/img/logo.svg";
import Modal from "../ui/Modal";
import { GlobalOnboardingTourBridge } from "../onboarding/OnboardingHeroCard";

import { useAuth } from "../../auth/authStore";
import { chatApi } from "../../api/chatApi";
import { profileApi } from "../../api/profileApi";
import { subscriptionsApi } from "../../api/subscriptionsApi";
import { useNotifications } from "../../notifications/notificationsStore";
import { featureKeyForCreatePath, useCreateSectionGate } from "../../subscription/useCreateSectionGate";
import { useToast } from "../../ui/toast/ToastProvider";
import { resolveUrl } from "../../utils/urls";
import {
  applySidebarPreferencesToItems,
  normalizeCustomSidebarItem,
  normalizeCustomSidebarSection,
  normalizeSidebarPreferences,
  isSidebarItemLocked,
  hasSidebarPreferencesLocal,
  readSidebarPreferencesLocal,
  serializeSidebarPreferencesForApi,
  sidebarNavId,
  writeSidebarPreferencesLocal,
  defaultCustomSidebarSection,
} from "../../layout/sidebarPreferences";
import { FINANCE_SECTION_ENABLED } from "../../config/env";

const PRIVILEGED_ROLES = new Set(["ADMIN", "PSYCHOLOGIST", "MODERATOR"]);
const HIDDEN_SIDEBAR_SECTION_KEYS = new Set(["group-sessions", "podcasts", "literature"]);

function initials(name) {
  const parsed = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk[0])
    .join("");
  return parsed ? parsed.toUpperCase() : "BS";
}

function normalizeRoleToken(value) {
  return String(value || "")
    .replace(/^ROLE_/, "")
    .trim()
    .toUpperCase();
}

function roleLabelRu(value, fallbackName = "") {
  const token = normalizeRoleToken(value);
  if (token === "ADMIN") return "Администратор";
  if (token === "PSYCHOLOGIST") return "Психолог";
  if (token === "MODERATOR") return "Модератор";
  if (token === "CLIENT") return "Клиент";

  const text = String(value || "").trim();
  if (text) return text;

  if (String(fallbackName || "").trim() === "Гость") return "Гость";
  return "Пользователь";
}

function dashboardLabelByRole(roleToken) {
  return "Сегодня";
}

function planLabelRu(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const token = raw.toUpperCase();
  if (token === "FREE") return "Бесплатный";
  if (token === "BASIC") return "Базовый";
  if (token === "PRO") return "Расширенный";
  return raw;
}

function subscriptionStatusRu(value, trialActive = false) {
  if (trialActive) return "Пробный период";
  const token = String(value || "").trim().toUpperCase();
  if (!token || token === "UNKNOWN") return "";
  if (token.includes("ACTIVE") || token.includes("PAID") || token.includes("SUCCESS")) return "Подписка активна";
  if (token.includes("PENDING") || token.includes("WAIT")) return "Ожидает оплаты";
  if (token.includes("CANCEL")) return "Подписка отменена";
  if (token.includes("EXPIRE")) return "Подписка истекла";
  if (token.includes("PAST_DUE") || token.includes("DEBT") || token.includes("UNPAID")) return "Есть задолженность";
  return "";
}

function hasMeaningfulSubscriptionState(state) {
  if (!state || typeof state !== "object") return false;
  const token = String(state.status || "").trim().toUpperCase();
  if (token && token !== "UNKNOWN") return true;
  const raw = state.raw && typeof state.raw === "object" ? state.raw : null;
  return Boolean(raw && Object.keys(raw).length > 0);
}

function meSubscriptionStatus(me) {
  return (
    me?.subscriptionStatus ||
    me?.statusSubscription ||
    me?.planStatus ||
    me?.tariffStatus ||
    me?.billingStatus ||
    ""
  );
}

function mePlanLabel(me) {
  return (
    me?.planLabel ||
    me?.planName ||
    me?.subscriptionPlan ||
    me?.tariffName ||
    me?.tariff ||
    me?.plan ||
    ""
  );
}

function meTrialFlag(me) {
  const raw = me?.trialActive ?? me?.isTrial ?? me?.onTrial ?? null;
  if (typeof raw === "boolean") return raw;
  return false;
}

function detectContextKey(pathname = "") {
  if (/^\/dashboard\/podcasts(?:\/.*)?$/.test(pathname) || /^\/podcasts(?:\/.*)?$/.test(pathname)) return "podcasts";
  if (/^\/dashboard\/literature(?:\/.*)?$/.test(pathname) || /^\/literature(?:\/.*)?$/.test(pathname)) return "literature";
  if (/^\/dashboard\/blog(?:\/.*)?$/.test(pathname) || /^\/blog(?:\/.*)?$/.test(pathname)) return "blog";
  if (/^\/dashboard\/tests(?:\/.*)?$/.test(pathname)) return "tests";
  if (/^\/dashboard\/notes(?:\/.*)?$/.test(pathname)) return "notes";
  if (/^\/dashboard\/group-sessions(?:\/.*)?$/.test(pathname)) return "group-sessions";
  if (/^\/dashboard\/conferences(?:\/.*)?$/.test(pathname)) return "conferences";
  if (/^\/forum(?:\/.*)?$/.test(pathname)) return "forum";
  if (/^\/chat(?:\/.*)?$/.test(pathname)) return "chat";
  if (/^\/notifications(?:\/.*)?$/.test(pathname)) return "notifications";
  return "cabinet";
}

function IconGrid() {
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="2" width="7" height="7" rx="1.5" />
      <rect x="11" y="2" width="7" height="7" rx="1.5" />
      <rect x="2" y="11" width="7" height="7" rx="1.5" />
      <rect x="11" y="11" width="7" height="7" rx="1.5" />
    </svg>
  );
}

function IconCalendar() {
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="4" width="14" height="13" rx="1.5" />
      <path d="M3 8h14" />
      <path d="M7 2v3M13 2v3" />
    </svg>
  );
}

function IconConference() {
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
      <path d="M7 8.5l2.6 1.5v-3l-2.6 1.5z" fill="currentColor" stroke="none" />
      <path d="M11.5 7.2l3-1.7v6.9l-3-1.7z" />
    </svg>
  );
}

function IconChat() {
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6l-4 3V4z" />
    </svg>
  );
}

function IconUsers() {
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="7" cy="7" r="3.5" />
      <path d="M1 18c0-3.3 2.7-6 6-6s6 2.7 6 6" />
      <circle cx="15" cy="7" r="2.5" opacity="0.5" />
    </svg>
  );
}

function IconProfile() {
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="10" cy="6.5" r="3.2" />
      <path d="M3.8 17.5c.8-4 3.1-6 6.2-6s5.4 2 6.2 6" strokeLinecap="round" />
      <path d="M13.8 3.8l1.4-1.4M15.6 6.2h2" opacity="0.45" strokeLinecap="round" />
    </svg>
  );
}

function IconGroupSession() {
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2.5" y="3.5" width="15" height="13" rx="2" />
      <path d="M6 7h8M6 10h5" strokeLinecap="round" />
      <circle cx="6.5" cy="14.5" r="1.4" />
      <circle cx="10" cy="14.5" r="1.4" />
      <circle cx="13.5" cy="14.5" r="1.4" />
    </svg>
  );
}

function IconJournal() {
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="10" cy="9" r="3.5" />
      <path d="M3.5 17.5c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5" />
    </svg>
  );
}

function IconNotes() {
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 2.5H5a1.8 1.8 0 0 0-1.8 1.8v11.4A1.8 1.8 0 0 0 5 17.5h10a1.8 1.8 0 0 0 1.8-1.8V7.3L12 2.5Z" />
      <path d="M12 2.5v4.8h4.8" />
      <path d="M6.6 10h6.8M6.6 13h5" strokeLinecap="round" />
    </svg>
  );
}

function IconPodcast() {
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="10" cy="9" r="3.5" />
      <path d="M3.5 17.5c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5" />
      <path d="M10 9V5M8 3h4" />
    </svg>
  );
}

function IconBooks() {
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="2" width="10" height="14" rx="1.5" />
      <rect x="13" y="4" width="3" height="12" rx="1.5" opacity="0.5" />
      <path d="M6 5h4M6 8h4M6 11h2.5" strokeLinecap="round" />
    </svg>
  );
}

function IconForum() {
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6l-4 3V5z" opacity="0.6" />
      <path d="M2 5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H6l-4 3V5z" />
    </svg>
  );
}

function IconFinance() {
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
      <path d="M2.5 8.3h15" />
      <path d="M6 12h3.5" strokeLinecap="round" />
      <path d="M12.2 12h1.8" strokeLinecap="round" />
    </svg>
  );
}

function IconCabinet() {
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="4" width="14" height="13" rx="1.5" />
      <path d="M7 9h6M7 12h4" />
    </svg>
  );
}

function IconBell() {
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M16 8a6 6 0 1 0-12 0c0 5-2 6.5-2 6.5h16S16 13 16 8z" />
      <path d="M12 17a2 2 0 0 1-4 0" />
    </svg>
  );
}

function IconSettings() {
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="10" cy="10" r="2.5" />
      <path d="M10 2.5v2M10 15.5v2M17.5 10h-2M4.5 10h-2M15.3 4.7l-1.4 1.4M6.1 13.9l-1.4 1.4M15.3 15.3l-1.4-1.4M6.1 6.1L4.7 4.7" strokeLinecap="round" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M10 2.2l6 2.3v4.4c0 4.1-2.4 6.8-6 8.9-3.6-2.1-6-4.8-6-8.9V4.5L10 2.2z" />
      <path d="M7.4 10.1l1.7 1.7 3.5-3.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconAdd() {
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M10 4v12M4 10h12" strokeLinecap="round" />
    </svg>
  );
}

function IconSupport() {
  return (
    <svg className="podcasts-sidebar__support-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 12a8 8 0 0 1 16 0" />
      <path d="M4.5 12.5v3.1a2.4 2.4 0 0 0 2.4 2.4H8a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1H6.9a2.4 2.4 0 0 0-2.4 2.5Z" />
      <path d="M19.5 12.5v3.1a2.4 2.4 0 0 1-2.4 2.4H16a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1h1.1a2.4 2.4 0 0 1 2.4 2.5Z" />
    </svg>
  );
}

function isActiveItem(item, location, activeKey) {
  if (typeof item.isActive === "function") {
    return item.isActive(location, activeKey);
  }
  if (item.key) return item.key === activeKey;
  return false;
}

function dedupeNavItems(items = []) {
  const seen = new Set();
  return items.filter((item) => {
    const key = String(item?.to || `label:${String(item?.label || "")}`);
    if (!key) return false;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function buildPlatformItems({ roleToken, notificationsCount, chatBadge = 0 }) {
  const isClient = roleToken === "CLIENT";

  if (isClient) {
    return [
      {
        key: "chat",
        to: "/chat",
        label: "Чаты",
        icon: IconChat,
        badge: Number(chatBadge) > 0 ? Number(chatBadge) : undefined,
        isActive: (location, activeKey) => activeKey === "chat" || location.pathname.startsWith("/chat"),
      },
      {
        key: "conferences",
        to: "/dashboard/conferences",
        label: "Конференции",
        icon: IconConference,
        isActive: (location) => location.pathname.startsWith("/dashboard/conferences"),
      },
      {
        key: "notifications",
        to: "/notifications",
        label: "Уведомления",
        icon: IconBell,
        badge: notificationsCount,
        isActive: (location, activeKey) => activeKey === "notifications" || location.pathname.startsWith("/notifications"),
      },
    ];
  }

  const profileTo = roleToken === "PSYCHOLOGIST" ? "/dashboard/settings?tab=psychologist" : "/dashboard/settings?tab=profile";

  return [
    {
      key: "dashboard",
      to: "/dashboard",
      label: dashboardLabelByRole(roleToken),
      icon: IconGrid,
      isActive: (location) => location.pathname === "/dashboard" && !location.hash,
    },
    {
      key: "profile",
      to: profileTo,
      label: "Профиль",
      icon: IconProfile,
      isActive: (location) =>
        location.pathname === "/dashboard/settings" &&
        (!location.search || location.search.includes("tab=profile") || location.search.includes("tab=psychologist")),
    },
    {
      key: "calendar",
      to: "/dashboard/calendar",
      label: "Календарь",
      icon: IconCalendar,
      isActive: (location) =>
        location.pathname === "/dashboard/calendar" || location.pathname.startsWith("/dashboard/calendar/"),
    },
    {
      key: "chat",
      to: "/chat",
      label: "Чаты",
      icon: IconChat,
      badge: Number(chatBadge) > 0 ? Number(chatBadge) : undefined,
      isActive: (location, activeKey) => activeKey === "chat" || location.pathname.startsWith("/chat"),
    },
    {
      key: "conferences",
      to: "/dashboard/conferences",
      label: "Конференции",
      icon: IconConference,
      isActive: (location) => location.pathname.startsWith("/dashboard/conferences"),
    },
    ...((roleToken === "PSYCHOLOGIST" || roleToken === "PSYCHO")
      ? [
        {
          key: "clients",
          to: "/dashboard/clients",
          label: "Клиенты",
          icon: IconUsers,
          isActive: (location) =>
            location.pathname === "/dashboard/clients" || location.pathname.startsWith("/dashboard/clients/"),
        },
        {
          key: "notes",
          to: "/dashboard/notes",
          label: "Заметки",
          icon: IconNotes,
          isActive: (location, activeKey) => activeKey === "notes" || location.pathname.startsWith("/dashboard/notes"),
        },
      ]
      : []),
    {
      key: "notifications",
      to: "/notifications",
      label: "Уведомления",
      icon: IconBell,
      badge: notificationsCount,
      isActive: (location, activeKey) => activeKey === "notifications" || location.pathname.startsWith("/notifications"),
    },
  ];
}

function buildClientItems({ roleToken }) {
  if (roleToken !== "CLIENT") return [];

  const items = [
    {
      key: "dashboard",
      to: "/dashboard",
      label: dashboardLabelByRole(roleToken),
      icon: IconCabinet,
      isActive: (location) => location.pathname === "/dashboard" && !location.hash,
    },
    {
      key: "profile",
      to: "/dashboard/settings?tab=profile",
      label: "Профиль",
      icon: IconProfile,
      isActive: (location) =>
        location.pathname === "/dashboard/settings" &&
        (!location.search || location.search.includes("tab=profile")),
    },
    {
      key: "appointments",
      to: "/my-appointments",
      label: "Мои записи",
      icon: IconCalendar,
      isActive: (location) =>
        location.pathname === "/my-appointments" ||
        (location.pathname === "/dashboard/settings" && location.search.includes("tab=appointments")),
    },
    {
      key: "diary",
      to: "/dashboard/diary",
      label: "Дневник",
      icon: IconJournal,
      isActive: (location) => location.pathname.startsWith("/dashboard/diary"),
    },
    {
      key: "catalog",
      to: "/psychologist",
      label: "Подобрать психолога",
      icon: IconUsers,
      isActive: (location) =>
        location.pathname === "/catalog" ||
        location.pathname === "/psychologist" ||
        location.pathname === "/psychologists" ||
        location.pathname.startsWith("/psychologist/"),
    },
  ];

  return items;
}

function buildContentItems({ roleToken, isAuthenticated } = {}) {
  const canShowSubscription = Boolean(isAuthenticated);
  const items = [
    {
      key: "blog",
      to: "/blog",
      label: "Блог",
      icon: IconJournal,
      isActive: (location, activeKey) =>
        activeKey === "blog" ||
        location.pathname.startsWith("/blog") ||
        location.pathname.startsWith("/dashboard/blog"),
    },
    {
      key: "forum",
      to: "/forum",
      label: "Форум",
      icon: IconForum,
      isActive: (location, activeKey) => activeKey === "forum" || location.pathname.startsWith("/forum"),
    },
  ];

  if (canShowSubscription) {
    items.push({
      key: "subscription",
      to: "/dashboard/subscription",
      label: roleToken === "CLIENT" ? "Стать психологом" : "Подписка",
      icon: IconShield,
      isActive: (location) => location.pathname.startsWith("/dashboard/subscription"),
    });
  }

  return items;
}

function buildContextSection({ contextKey, roleToken, canOpenLiteratureEditor, isAuthenticated, location, chatBadge = 0 }) {
  const isPrivileged = PRIVILEGED_ROLES.has(roleToken);

  if (HIDDEN_SIDEBAR_SECTION_KEYS.has(contextKey)) {
    return null;
  }

  if (contextKey === "group-sessions") {
    return {
      label: "Групповые сессии",
      items: isPrivileged
        ? [
            {
              to: "/dashboard/group-sessions",
              label: "Все сессии",
              icon: IconGroupSession,
              isActive: (loc) =>
                loc.pathname === "/dashboard/group-sessions" && !new URLSearchParams(loc.search || "").get("create"),
            },
            {
              to: "/dashboard/group-sessions/my",
              label: "Мои сессии",
              icon: IconGrid,
              isActive: (loc) =>
                loc.pathname === "/dashboard/group-sessions/my" && new URLSearchParams(loc.search || "").get("create") !== "1",
            },
            {
              to: "/dashboard/group-sessions/my?create=1",
              label: "Создать сессию",
              icon: IconAdd,
              isActive: (loc) =>
                loc.pathname === "/dashboard/group-sessions/my" && new URLSearchParams(loc.search || "").get("create") === "1",
            },
          ]
        : [
            {
              to: "/dashboard/group-sessions",
              label: "Все сессии",
              icon: IconGroupSession,
              isActive: (loc) => loc.pathname.startsWith("/dashboard/group-sessions"),
            },
          ],
    };
  }

  if (contextKey === "podcasts") {
    return {
      label: "Подкасты",
      items: isPrivileged
        ? [
            {
              to: "/dashboard/podcasts",
              label: "Мои подкасты",
              icon: IconPodcast,
              isActive: (location) =>
                location.pathname.startsWith("/dashboard/podcasts") &&
                !location.pathname.startsWith("/dashboard/podcasts/new"),
            },
            {
              to: "/dashboard/podcasts/new",
              label: "Добавить подкаст",
              icon: IconAdd,
              isActive: (location) => location.pathname.startsWith("/dashboard/podcasts/new"),
            },
            {
              to: "/podcasts",
              label: "Каталог подкастов",
              icon: IconJournal,
              isActive: (location) => location.pathname.startsWith("/podcasts"),
            },
          ]
        : [
            {
              to: "/podcasts",
              label: "Каталог подкастов",
              icon: IconPodcast,
              isActive: (location) => location.pathname.startsWith("/podcasts"),
            },
          ],
    };
  }

  if (contextKey === "literature") {
    const pathname = String(location?.pathname || "");
    const hash = String(location?.hash || "");
    const isBookDetails = /^\/literature\/books\/.+/.test(pathname);
    const catalogItem = {
      to: "/literature#books-catalog",
      label: "Каталог книг",
      icon: IconBooks,
      isActive: (loc) => loc.pathname === "/literature" && (!loc.hash || loc.hash === "#books-catalog"),
    };
    const contextItems = [catalogItem];

    if (isBookDetails) {
      contextItems.push(
        {
          to: `${pathname}#book-about`,
          label: "О книге",
          icon: IconJournal,
          isActive: (loc) => loc.pathname === pathname && (!loc.hash || loc.hash === "#book-about"),
        },
        {
          to: `${pathname}#book-contents`,
          label: "Содержание",
          icon: IconGrid,
          isActive: (loc) => loc.pathname === pathname && loc.hash === "#book-contents",
        },
        {
          to: `${pathname}#book-reviews`,
          label: "Отзывы",
          icon: IconChat,
          isActive: (loc) => loc.pathname === pathname && loc.hash === "#book-reviews",
        }
      );
    }

    if (canOpenLiteratureEditor || isPrivileged) {
      contextItems.push(
        {
          to: "/literature#my-books-section",
          label: "Мои книги",
          icon: IconGrid,
          isActive: (loc) =>
            loc.pathname === "/literature" &&
            (loc.hash === "#my-books-section" || (hash === "#my-books-section" && loc.hash === "")),
        },
        {
          to: "/dashboard/literature/new",
          label: "Добавить книгу",
          icon: IconAdd,
          isActive: (loc) => loc.pathname.startsWith("/dashboard/literature/new"),
        }
      );
    } else if (isAuthenticated) {
      contextItems.push({
        to: "/literature#saved-books",
        label: "Сохранённые",
        icon: IconBooks,
        isActive: (loc) =>
          loc.pathname === "/literature" &&
          (loc.hash === "#saved-books" || (hash === "#saved-books" && loc.hash === "")),
      });
    }

    return {
      label: "Литература",
      items: contextItems,
    };
  }

  if (contextKey === "blog") {
    return {
      label: "Блог",
      items: isPrivileged
        ? [
            {
              to: "/blog",
              label: "Все статьи",
              icon: IconJournal,
              isActive: (location) => location.pathname.startsWith("/blog"),
            },
            {
              to: "/dashboard/blog",
              label: "Мои статьи",
              icon: IconGrid,
              isActive: (location) =>
                location.pathname === "/dashboard/blog" ||
                (location.pathname.startsWith("/dashboard/blog/") && !location.pathname.startsWith("/dashboard/blog/new")),
            },
            {
              to: "/dashboard/blog/new",
              label: "Новая статья",
              icon: IconAdd,
              isActive: (location) => location.pathname.startsWith("/dashboard/blog/new"),
            },
          ]
        : [
            {
              to: "/blog",
              label: "Все статьи",
              icon: IconJournal,
              isActive: (location) => location.pathname.startsWith("/blog"),
            },
          ],
    };
  }

  if (contextKey === "tests" && isPrivileged) {
    return {
      label: "Тесты",
      items: [
        {
          to: "/dashboard/tests",
          label: "Мои тесты",
          icon: IconGrid,
          isActive: (location) => location.pathname.startsWith("/dashboard/tests"),
        },
        {
          to: "/chat",
          label: "Чаты с клиентами",
          icon: IconChat,
          isActive: (location) => location.pathname.startsWith("/chat"),
        },
      ],
    };
  }

  if (contextKey === "notes") {
    return {
      label: "Заметки",
      items: [
        {
          to: "/dashboard/notes",
          label: "Заметки клиентов",
          icon: IconNotes,
          isActive: (location) => location.pathname.startsWith("/dashboard/notes"),
        },
        {
          to: "/dashboard/clients",
          label: "Клиенты",
          icon: IconUsers,
          isActive: (location) => location.pathname.startsWith("/dashboard/clients"),
        },
        {
          to: "/dashboard/calendar",
          label: "Календарь",
          icon: IconCalendar,
          isActive: (location) => location.pathname.startsWith("/dashboard/calendar"),
        },
      ],
    };
  }

  if (contextKey === "forum") {
    const items = [
      {
        to: "/forum",
        label: "Все разделы",
        icon: IconForum,
        isActive: (location) => location.pathname === "/forum" || location.pathname.startsWith("/forum/sections/"),
      },
    ];

    if (isAuthenticated) {
      items.push(
        {
          to: "/forum/my/topics",
          label: "Мои темы",
          icon: IconJournal,
          isActive: (location) => location.pathname === "/forum/my/topics",
        },
        {
          to: "/forum/my/comments",
          label: "Мои комментарии",
          icon: IconChat,
          isActive: (location) => location.pathname === "/forum/my/comments",
        },
        {
          to: "/forum?compose=1",
          label: "Новая тема",
          icon: IconAdd,
          isActive: (location) =>
            location.pathname === "/forum" && new URLSearchParams(location.search).get("compose") === "1",
        }
      );
    }

    return {
      label: "Форум",
      items,
    };
  }

  if (contextKey === "chat" || contextKey === "notifications") {
    return {
      label: "Связь",
      items: [
        {
          to: "/chat",
          label: "Чаты",
          icon: IconChat,
          badge: Number(chatBadge) > 0 ? Number(chatBadge) : undefined,
          isActive: (location) => location.pathname.startsWith("/chat"),
        },
        {
          to: "/notifications",
          label: "Уведомления",
          icon: IconBell,
          isActive: (location) => location.pathname.startsWith("/notifications"),
        },
      ],
    };
  }

  if (contextKey === "conferences") {
    return {
      label: "Конференции",
      items: [
        {
          to: "/dashboard/conferences",
          label: "Все конференции",
          icon: IconConference,
          isActive: (location) => location.pathname.startsWith("/dashboard/conferences"),
        },
        {
          to: "/chat",
          label: "Общий чат",
          icon: IconChat,
          badge: Number(chatBadge) > 0 ? Number(chatBadge) : undefined,
          isActive: (location) => location.pathname.startsWith("/chat"),
        },
      ],
    };
  }

  return {
    label: "Сегодня",
    items: [
      {
        to: "/dashboard",
        label: dashboardLabelByRole(roleToken),
        icon: IconCabinet,
        isActive: (location) => location.pathname === "/dashboard" && !location.hash,
      },
    ],
  };
}


function extractSidebarPreferencesFromResponse(raw = {}) {
  if (!raw || typeof raw !== "object") return null;
  return raw.sidebar || raw.sidebarSettings || raw.sidebarPreferences || raw.navigation || null;
}

function buildCustomSidebarItems(customItems = []) {
  return (Array.isArray(customItems) ? customItems : [])
    .map((item, index) => normalizeCustomSidebarItem(item, index))
    .filter(Boolean)
    .map((item) => ({
      key: item.id,
      navId: item.id,
      to: item.to,
      label: item.label,
      icon: IconGrid,
      custom: true,
      sectionId: item.sectionId,
      isActive: (location) => location.pathname === item.to || `${location.pathname}${location.search || ""}${location.hash || ""}` === item.to,
    }));
}

function itemMetaForEditor(sectionKey, item = {}, sectionLabel = "", menuIndex = 0) {
  const navId = sidebarNavId(sectionKey, item);
  const baseEditorSectionId = sectionKey === "custom" ? (item.sectionId || "custom-section:main") : sectionKey;
  const editorSectionId = item.editorSectionId || baseEditorSectionId;
  return {
    navId,
    sectionKey,
    sectionLabel,
    baseEditorSectionId,
    editorSectionId,
    menuIndex: Number.isFinite(Number(menuIndex)) ? Number(menuIndex) : 0,
    label: item.label || navId,
    to: item.to || "",
    locked: isSidebarItemLocked(sectionKey, item),
    custom: Boolean(item.custom),
    sectionId: item.sectionId || "",
  };
}

function orderEditorItems(items = [], draft = {}) {
  const sourceItems = Array.isArray(items) ? items : [];
  const explicitOrder = Array.isArray(draft?.order) ? draft.order.filter(Boolean) : [];

  if (!explicitOrder.length) {
    return [...sourceItems].sort((a, b) => Number(a.menuIndex || 0) - Number(b.menuIndex || 0));
  }

  const orderIndex = new Map(explicitOrder.map((id, index) => [id, index]));
  return [...sourceItems].sort((a, b) => {
    const aHasOrder = orderIndex.has(a.navId);
    const bHasOrder = orderIndex.has(b.navId);

    if (aHasOrder && bHasOrder) return orderIndex.get(a.navId) - orderIndex.get(b.navId);
    if (aHasOrder) return -1;
    if (bHasOrder) return 1;

    return Number(a.menuIndex || 0) - Number(b.menuIndex || 0);
  });
}

function SidebarEditorModal({ open, draft, setDraft, items, onClose, onSave, onReset, saving }) {
  const [newLabel, setNewLabel] = React.useState("");
  const [newPath, setNewPath] = React.useState("");
  const [newSectionLabel, setNewSectionLabel] = React.useState("");
  const [newCustomSectionId, setNewCustomSectionId] = React.useState(defaultCustomSidebarSection().id);
  const [dragId, setDragId] = React.useState("");
  const [addNavId, setAddNavId] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      setNewLabel("");
      setNewPath("");
      setNewSectionLabel("");
      setNewCustomSectionId(defaultCustomSidebarSection().id);
      setDragId("");
      setAddNavId("");
    }
  }, [open]);

  const normalizedDraft = React.useMemo(() => normalizeSidebarPreferences(draft), [draft]);
  const customSectionOptions = React.useMemo(() => {
    const base = defaultCustomSidebarSection();
    const seen = new Set([base.id]);
    const extra = (Array.isArray(normalizedDraft.customSections) ? normalizedDraft.customSections : [])
      .map((section, index) => normalizeCustomSidebarSection(section, index))
      .filter(Boolean)
      .filter((section) => {
        if (seen.has(section.id)) return false;
        seen.add(section.id);
        return true;
      });
    return [base, ...extra];
  }, [normalizedDraft.customSections]);

  React.useEffect(() => {
    if (!customSectionOptions.some((section) => section.id === newCustomSectionId)) {
      setNewCustomSectionId(customSectionOptions[0]?.id || defaultCustomSidebarSection().id);
    }
  }, [customSectionOptions, newCustomSectionId]);

  const systemSectionOptions = React.useMemo(() => [
    { id: "client", label: "Клиент" },
    { id: "platform", label: "Платформа" },
    { id: "content", label: "Контент" },
  ], []);

  const sectionLabelById = React.useMemo(() => {
    const map = new Map();
    systemSectionOptions.forEach((section) => map.set(section.id, section.label));
    map.set("context", "Контекст раздела");
    customSectionOptions.forEach((section) => map.set(section.id, section.label));
    map.set("custom", "Моё меню");
    return map;
  }, [customSectionOptions, systemSectionOptions]);

  const orderedItems = React.useMemo(() => orderEditorItems(items, draft), [draft, items]);
  const hiddenSet = React.useMemo(() => new Set(Array.isArray(draft?.hiddenIds) ? draft.hiddenIds : []), [draft?.hiddenIds]);
  const addCandidates = React.useMemo(
    () => orderedItems.filter((item) => !item.locked && hiddenSet.has(item.navId)),
    [hiddenSet, orderedItems]
  );

  const groupedItems = React.useMemo(() => {
    const desiredOrder = ["client", "platform", "content", ...customSectionOptions.map((section) => section.id)];
    const customIds = new Set(customSectionOptions.map((section) => section.id));
    const groupMap = new Map();
    desiredOrder.forEach((id) => groupMap.set(id, { id, label: sectionLabelById.get(id) || id, items: [], custom: customIds.has(id) }));

    orderedItems.forEach((item) => {
      const groupId = item.editorSectionId || item.sectionKey || "custom-section:main";
      if (!groupMap.has(groupId)) {
        groupMap.set(groupId, { id: groupId, label: sectionLabelById.get(groupId) || item.sectionLabel || "Моё меню", items: [], custom: customIds.has(groupId) });
      }
      groupMap.get(groupId).items.push(item);
    });

    return Array.from(groupMap.values()).filter((group) => group.items.length > 0 || group.custom);
  }, [customSectionOptions, orderedItems, sectionLabelById]);

  React.useEffect(() => {
    if (!addNavId && addCandidates.length) {
      setAddNavId(addCandidates[0].navId);
      return;
    }
    if (addNavId && !addCandidates.some((item) => item.navId === addNavId)) {
      setAddNavId(addCandidates[0]?.navId || "");
    }
  }, [addCandidates, addNavId]);

  const setOrderByItems = React.useCallback((nextItems) => {
    setDraft((prev) => ({
      ...prev,
      order: nextItems.map((item) => item.navId),
    }));
  }, [setDraft]);

  const reorderWithinSection = React.useCallback((navId, targetNavIdOrDirection) => {
    const current = orderEditorItems(items, draft);
    const item = current.find((candidate) => candidate.navId === navId);
    if (!item) return;
    const sectionId = item.editorSectionId || item.sectionKey || "custom-section:main";
    const sameSection = current.filter((candidate) => (candidate.editorSectionId || candidate.sectionKey || "custom-section:main") === sectionId);
    const fromInSection = sameSection.findIndex((candidate) => candidate.navId === navId);
    if (fromInSection < 0) return;

    let toInSection = -1;
    if (typeof targetNavIdOrDirection === "number") {
      toInSection = fromInSection + targetNavIdOrDirection;
    } else {
      toInSection = sameSection.findIndex((candidate) => candidate.navId === targetNavIdOrDirection);
    }
    if (toInSection < 0 || toInSection >= sameSection.length || toInSection === fromInSection) return;

    const nextSameSection = [...sameSection];
    const [picked] = nextSameSection.splice(fromInSection, 1);
    nextSameSection.splice(toInSection, 0, picked);

    let pointer = 0;
    const next = current.map((candidate) => {
      const candidateSectionId = candidate.editorSectionId || candidate.sectionKey || "custom-section:main";
      if (candidateSectionId !== sectionId) return candidate;
      return nextSameSection[pointer++];
    });
    setOrderByItems(next);
  }, [draft, items, setOrderByItems]);

  const moveItem = React.useCallback((navId, direction) => {
    reorderWithinSection(navId, direction);
  }, [reorderWithinSection]);

  const toggleHidden = React.useCallback((item) => {
    if (item.locked) return;
    setDraft((prev) => {
      const current = new Set(Array.isArray(prev.hiddenIds) ? prev.hiddenIds : []);
      if (current.has(item.navId)) current.delete(item.navId);
      else current.add(item.navId);
      return { ...prev, hiddenIds: Array.from(current) };
    });
  }, [setDraft]);

  const showSelectedSystemItem = React.useCallback(() => {
    if (!addNavId) return;
    setDraft((prev) => {
      const hiddenIds = (Array.isArray(prev.hiddenIds) ? prev.hiddenIds : []).filter((id) => id !== addNavId);
      const currentOrder = Array.isArray(prev.order) ? prev.order : [];
      return {
        ...prev,
        hiddenIds,
        order: currentOrder.includes(addNavId) ? currentOrder : [...currentOrder, addNavId],
      };
    });
  }, [addNavId, setDraft]);

  const addCustomSection = React.useCallback(() => {
    const section = normalizeCustomSidebarSection({ id: `custom-section-${Date.now()}`, label: newSectionLabel });
    if (!section) return;
    setDraft((prev) => {
      const current = Array.isArray(prev.customSections) ? prev.customSections : [];
      if (current.some((candidate) => candidate.id === section.id || String(candidate.label || "").trim().toLowerCase() === section.label.toLowerCase())) {
        return prev;
      }
      return { ...prev, customSections: [...current, section] };
    });
    setNewSectionLabel("");
    setNewCustomSectionId(section.id);
  }, [newSectionLabel, setDraft]);

  const removeCustomSection = React.useCallback((sectionId) => {
    if (!sectionId || sectionId === defaultCustomSidebarSection().id) return;
    setDraft((prev) => {
      const nextAssignments = { ...(prev.sectionAssignments || {}) };
      Object.entries(nextAssignments).forEach(([navId, assignedSectionId]) => {
        if (assignedSectionId === sectionId) delete nextAssignments[navId];
      });
      return {
        ...prev,
        customSections: (Array.isArray(prev.customSections) ? prev.customSections : []).filter((section) => section.id !== sectionId),
        customItems: (Array.isArray(prev.customItems) ? prev.customItems : []).map((item) => (
          item.sectionId === sectionId ? { ...item, sectionId: defaultCustomSidebarSection().id } : item
        )),
        sectionAssignments: nextAssignments,
      };
    });
  }, [setDraft]);

  const updateCustomSectionLabel = React.useCallback((sectionId, label) => {
    if (!sectionId || sectionId === defaultCustomSidebarSection().id) return;
    const normalized = normalizeCustomSidebarSection({ id: sectionId, label });
    if (!normalized) return;
    setDraft((prev) => ({
      ...prev,
      customSections: (Array.isArray(prev.customSections) ? prev.customSections : []).map((section) => (
        section.id === sectionId ? { ...section, label: normalized.label } : section
      )),
    }));
  }, [setDraft]);

  const updateItemSection = React.useCallback((item, sectionId) => {
    if (!item?.navId) return;
    const validIds = new Set([item.baseEditorSectionId || item.sectionKey, ...customSectionOptions.map((section) => section.id)]);
    const targetSectionId = validIds.has(sectionId) ? sectionId : (item.baseEditorSectionId || item.sectionKey);
    setDraft((prev) => {
      const next = { ...prev, sectionAssignments: { ...(prev.sectionAssignments || {}) } };
      const isBackToBase = targetSectionId === (item.baseEditorSectionId || item.sectionKey);
      if (isBackToBase) delete next.sectionAssignments[item.navId];
      else next.sectionAssignments[item.navId] = targetSectionId;
      if (item.custom) {
        next.customItems = (Array.isArray(prev.customItems) ? prev.customItems : []).map((custom) => (
          custom.id === item.navId ? { ...custom, sectionId: targetSectionId.startsWith("custom-section:") ? targetSectionId : defaultCustomSidebarSection().id } : custom
        ));
      }
      return next;
    });
  }, [customSectionOptions, setDraft]);

  const addCustomItem = React.useCallback(() => {
    const item = normalizeCustomSidebarItem({
      id: `custom-${Date.now()}`,
      label: newLabel,
      to: newPath,
      sectionId: newCustomSectionId,
    });
    if (!item) return;
    setDraft((prev) => ({
      ...prev,
      customItems: [...(Array.isArray(prev.customItems) ? prev.customItems : []), item],
      order: [...(Array.isArray(prev.order) ? prev.order : []), item.id],
    }));
    setNewLabel("");
    setNewPath("");
  }, [newCustomSectionId, newLabel, newPath, setDraft]);

  const removeCustomItem = React.useCallback((item) => {
    if (!item.custom) return;
    setDraft((prev) => ({
      ...prev,
      customItems: (Array.isArray(prev.customItems) ? prev.customItems : []).filter((custom) => custom.id !== item.navId),
      hiddenIds: (Array.isArray(prev.hiddenIds) ? prev.hiddenIds : []).filter((id) => id !== item.navId),
      order: (Array.isArray(prev.order) ? prev.order : []).filter((id) => id !== item.navId),
    }));
  }, [setDraft]);

  const handleDropOn = React.useCallback((targetId) => {
    if (!dragId || dragId === targetId) return;
    const current = orderEditorItems(items, draft);
    const dragged = current.find((item) => item.navId === dragId);
    const target = current.find((item) => item.navId === targetId);
    if (!dragged || !target) return;
    const draggedSection = dragged.editorSectionId || dragged.sectionKey || "custom-section:main";
    const targetSection = target.editorSectionId || target.sectionKey || "custom-section:main";
    if (draggedSection !== targetSection) {
      setDragId("");
      return;
    }
    reorderWithinSection(dragId, targetId);
    setDragId("");
  }, [draft, dragId, items, reorderWithinSection]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Настройка бокового меню"
      size="lg"
      className="sidebar-editor-modal-shell"
      cardClassName="sidebar-editor-modal-card"
      actions={(
        <>
          <button type="button" className="sidebar-editor__btn sidebar-editor__btn--ghost" onClick={onReset} disabled={saving}>
            Сбросить
          </button>
          <button type="button" className="sidebar-editor__btn sidebar-editor__btn--ghost" onClick={onClose} disabled={saving}>
            Отмена
          </button>
          <button type="button" className="sidebar-editor__btn sidebar-editor__btn--primary" onClick={onSave} disabled={saving}>
            {saving ? "Сохраняем…" : "Сохранить"}
          </button>
        </>
      )}
    >
      <div className="sidebar-editor">
        <div className="sidebar-editor__hint">
          Настройте sidebar под свой рабочий ритм: выберите ширину, плотность, служебные блоки, порядок пунктов и свои группы. Настройки сохраняются локально и синхронизируются с профилем.
        </div>

        <section className="sidebar-editor__appearance" aria-label="Внешний вид бокового меню">
          <div className="sidebar-editor__appearance-head">
            <div>
              <div className="sidebar-editor__system-title">Вид меню</div>
              <div className="sidebar-editor__system-note">На мобильном кнопка в topbar открывает меню, а режим с наведением раскрывает sidebar автоматически.</div>
            </div>
          </div>

          <div className="sidebar-editor__mode-grid" role="radiogroup" aria-label="Режим бокового меню">
            {[
              { value: "full", title: "Широкое", text: "Логотип, заголовки и подписи пунктов." },
              { value: "compact", title: "Компактное", text: "Иконки занимают минимум места, подсказки остаются." },
              { value: "hidden", title: "При наведении", text: "По умолчанию видны иконки, а sidebar раскрывается, когда курсор над меню." },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                className={`sidebar-editor__mode-card ${normalizedDraft.mode === option.value ? "is-active" : ""}`}
                onClick={() => setDraft((prev) => ({ ...prev, mode: option.value, collapsed: false }))}
                role="radio"
                aria-checked={normalizedDraft.mode === option.value}
              >
                <span className="sidebar-editor__mode-preview" data-mode={option.value}>
                  <i /><i /><i />
                </span>
                <span className="sidebar-editor__mode-title">{option.title}</span>
                <span className="sidebar-editor__mode-text">{option.text}</span>
              </button>
            ))}
          </div>

          <div className="sidebar-editor__quick-options">
            <div className="sidebar-editor__segmented" aria-label="Плотность меню">
              <span className="sidebar-editor__segmented-label">Плотность</span>
              <button
                type="button"
                className={normalizedDraft.density === "comfortable" ? "is-active" : ""}
                onClick={() => setDraft((prev) => ({ ...prev, density: "comfortable" }))}
              >
                Спокойная
              </button>
              <button
                type="button"
                className={normalizedDraft.density === "compact" ? "is-active" : ""}
                onClick={() => setDraft((prev) => ({ ...prev, density: "compact" }))}
              >
                Компактная
              </button>
            </div>

            <label className="sidebar-editor__switch">
              <input type="checkbox" checked={normalizedDraft.showSectionLabels} onChange={(event) => setDraft((prev) => ({ ...prev, showSectionLabels: event.target.checked }))} />
              <span />
              <b>Заголовки</b>
            </label>
            <label className="sidebar-editor__switch">
              <input type="checkbox" checked={normalizedDraft.showBadges} onChange={(event) => setDraft((prev) => ({ ...prev, showBadges: event.target.checked }))} />
              <span />
              <b>Бейджи</b>
            </label>
            <label className="sidebar-editor__switch">
              <input type="checkbox" checked={normalizedDraft.showSupport} onChange={(event) => setDraft((prev) => ({ ...prev, showSupport: event.target.checked }))} />
              <span />
              <b>Поддержка</b>
            </label>
            <label className="sidebar-editor__switch">
              <input type="checkbox" checked={normalizedDraft.showUserCard} onChange={(event) => setDraft((prev) => ({ ...prev, showUserCard: event.target.checked }))} />
              <span />
              <b>Профиль снизу</b>
            </label>
          </div>
        </section>

        <div className="sidebar-editor__system-add">
          <div>
            <div className="sidebar-editor__system-title">Добавить системный раздел</div>
            <div className="sidebar-editor__system-note">В списке только разделы, которые сейчас скрыты.</div>
          </div>
          <select value={addNavId} onChange={(event) => setAddNavId(event.target.value)} disabled={!addCandidates.length}>
            {addCandidates.length ? addCandidates.map((item) => (
              <option key={item.navId} value={item.navId}>{item.label} · {item.sectionLabel}</option>
            )) : <option value="">Все доступные разделы уже в меню</option>}
          </select>
          <button type="button" onClick={showSelectedSystemItem} disabled={!addNavId}>+ Добавить</button>
        </div>

        <details className="sidebar-editor__advanced-add">
          <summary>Свои заголовки и внутренние пункты</summary>
          <div className="sidebar-editor__section-builder">
            <div className="sidebar-editor__section-form">
              <input
                type="text"
                value={newSectionLabel}
                onChange={(event) => setNewSectionLabel(event.target.value)}
                placeholder="Название заголовка, например Рабочее"
                maxLength={32}
              />
              <button type="button" onClick={addCustomSection}>+ Заголовок</button>
            </div>

            {customSectionOptions.length > 1 ? (
              <div className="sidebar-editor__custom-sections">
                {customSectionOptions.filter((section) => section.id !== defaultCustomSidebarSection().id).map((section) => (
                  <div className="sidebar-editor__section-row" key={section.id}>
                    <input
                      type="text"
                      value={section.label}
                      onChange={(event) => updateCustomSectionLabel(section.id, event.target.value)}
                      maxLength={32}
                      aria-label={`Название заголовка ${section.label}`}
                    />
                    <button type="button" onClick={() => removeCustomSection(section.id)} aria-label={`Удалить заголовок ${section.label}`}>Удалить</button>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="sidebar-editor__add">
              <input
                type="text"
                value={newLabel}
                onChange={(event) => setNewLabel(event.target.value)}
                placeholder="Название пункта"
                maxLength={36}
              />
              <input
                type="text"
                value={newPath}
                onChange={(event) => setNewPath(event.target.value)}
                placeholder="Внутренний путь, например /dashboard/diary"
                maxLength={220}
              />
              <select value={newCustomSectionId} onChange={(event) => setNewCustomSectionId(event.target.value)}>
                {customSectionOptions.map((section) => (
                  <option key={section.id} value={section.id}>{section.label}</option>
                ))}
              </select>
              <button type="button" onClick={addCustomItem}>Добавить</button>
            </div>
          </div>
        </details>

        <div className="sidebar-editor__list" role="list">
          {groupedItems.map((group) => (
            <section className="sidebar-editor__group" key={group.id}>
              <div className="sidebar-editor__group-title">
                <span>{group.label}</span>
                {group.custom ? <small>сюда можно переносить пункты через выбор заголовка</small> : null}
              </div>
              <div className="sidebar-editor__group-list">
                {group.items.length === 0 ? <div className="sidebar-editor__empty-section">Пока пусто. Перенесите сюда нужные пункты или добавьте внутреннюю ссылку.</div> : null}
                {group.items.map((item, index) => {
                  const isHidden = !item.locked && hiddenSet.has(item.navId);
                  const isFirst = index === 0;
                  const isLast = index === group.items.length - 1;
                  return (
                    <div
                      key={item.navId}
                      className={`sidebar-editor__item ${isHidden ? "is-hidden" : ""} ${item.locked ? "is-locked" : ""}`}
                      draggable
                      onDragStart={() => setDragId(item.navId)}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => handleDropOn(item.navId)}
                      role="listitem"
                    >
                      <div className="sidebar-editor__drag" title="Перетащить внутри заголовка">⋮⋮</div>
                      <div className="sidebar-editor__meta">
                        <div className="sidebar-editor__title">{item.label}</div>
                        <div className="sidebar-editor__sub">
                          <span>{item.sectionLabel}</span>
                          {item.to ? <span>{item.to}</span> : null}
                          {item.locked ? <span>обязательный</span> : null}
                          {item.custom ? <span>свой пункт</span> : null}
                          {isHidden ? <span>скрыт</span> : null}
                        </div>
                        <select
                          className="sidebar-editor__section-select"
                          value={item.editorSectionId || item.baseEditorSectionId || item.sectionKey}
                          onChange={(event) => updateItemSection(item, event.target.value)}
                          aria-label="Перенести пункт в заголовок"
                        >
                          <option value={item.baseEditorSectionId || item.sectionKey}>Исходный: {item.sectionLabel}</option>
                          {customSectionOptions.map((section) => (
                            <option key={section.id} value={section.id}>{section.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="sidebar-editor__actions-inline">
                        <button type="button" className="sidebar-editor__icon-btn" onClick={() => moveItem(item.navId, -1)} disabled={isFirst} aria-label="Выше" title="Выше">↑</button>
                        <button type="button" className="sidebar-editor__icon-btn" onClick={() => moveItem(item.navId, 1)} disabled={isLast} aria-label="Ниже" title="Ниже">↓</button>
                        <button type="button" className="sidebar-editor__pill-btn" onClick={() => toggleHidden(item)} disabled={item.locked} title={isHidden ? "Вернуть в меню" : "Скрыть из меню"}>
                          {isHidden ? "Показать" : "Скрыть"}
                        </button>
                        {item.custom ? <button type="button" className="sidebar-editor__pill-btn is-danger" onClick={() => removeCustomItem(item)}>Удалить</button> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
    </Modal>
  );
}

export default function AppSidebarV2({
  activeKey = "",
  mobileOpen = false,
  userName,
  roleLabel,
  canOpenLiteratureEditor,
  notificationsBadge,
  chatBadge,
  onCloseMobile,
  onLogout,
  extraContent,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { me, role, logout, isAuthenticated } = useAuth();
  const sidebarStorageScope = String(me?.id || normalizeRoleToken(role || roleLabel) || "guest").trim() || "guest";
  const { unreadNotificationsCount = 0, messageUnreadTotal = 0 } = useNotifications();
  const { checkCreateAccess, limitModal } = useCreateSectionGate();
  const [subscriptionState, setSubscriptionState] = React.useState(null);
  const [supportModalOpen, setSupportModalOpen] = React.useState(false);
  const [supportKind, setSupportKind] = React.useState("SUPPORT");
  const [supportDraft, setSupportDraft] = React.useState("");
  const [actionMessageSending, setActionMessageSending] = React.useState(false);
  const [sidebarPreferences, setSidebarPreferences] = React.useState(() => readSidebarPreferencesLocal(sidebarStorageScope));
  const [sidebarEditorOpen, setSidebarEditorOpen] = React.useState(false);
  const [sidebarDraft, setSidebarDraft] = React.useState(() => readSidebarPreferencesLocal(sidebarStorageScope));
  const [sidebarSaving, setSidebarSaving] = React.useState(false);

  React.useEffect(() => {
    const normalized = normalizeSidebarPreferences(sidebarPreferences);
    const mode = normalized.mode || "full";
    const density = normalized.density || "comfortable";
    const modeClasses = ["bs-sidebar-mode-full", "bs-sidebar-mode-compact", "bs-sidebar-mode-hidden"];
    const densityClasses = ["bs-sidebar-density-comfortable", "bs-sidebar-density-compact"];

    if (typeof document !== "undefined") {
      const targets = [document.documentElement, document.body].filter(Boolean);
      targets.forEach((target) => {
        modeClasses.forEach((className) => target.classList.remove(className));
        densityClasses.forEach((className) => target.classList.remove(className));
        target.classList.add(`bs-sidebar-mode-${mode}`);
        target.classList.add(`bs-sidebar-density-${density}`);
        target.classList.remove("bs-sidebar-collapsed");
      });
    }
    return () => {
      if (typeof document !== "undefined") {
        const targets = [document.documentElement, document.body].filter(Boolean);
        targets.forEach((target) => {
          modeClasses.forEach((className) => target.classList.remove(className));
          densityClasses.forEach((className) => target.classList.remove(className));
          target.classList.remove("bs-sidebar-collapsed");
        });
      }
    };
  }, [sidebarPreferences]);

  React.useEffect(() => {
    let cancelled = false;
    const local = readSidebarPreferencesLocal(sidebarStorageScope);
    setSidebarPreferences(local);
    setSidebarDraft(local);

    if (!isAuthenticated) return () => { cancelled = true; };
    if (hasSidebarPreferencesLocal(sidebarStorageScope)) return () => { cancelled = true; };

    (async () => {
      try {
        const response = await profileApi.getUiSettings();
        if (cancelled) return;
        const serverSidebar = extractSidebarPreferencesFromResponse(response);
        if (!serverSidebar) return;
        const normalized = normalizeSidebarPreferences(serverSidebar);
        writeSidebarPreferencesLocal(sidebarStorageScope, normalized);
        setSidebarPreferences(normalized);
        setSidebarDraft(normalized);
      } catch {
        // Local settings are enough for UI; server sync is best-effort.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, me?.id, sidebarStorageScope]);

  const persistSidebarPreferences = React.useCallback(async (nextPreferences, { silent = false } = {}) => {
    const normalized = normalizeSidebarPreferences(nextPreferences);
    writeSidebarPreferencesLocal(sidebarStorageScope, normalized);
    setSidebarPreferences(normalized);
    setSidebarDraft(normalized);

    if (!isAuthenticated) return normalized;

    setSidebarSaving(true);
    try {
      await profileApi.updateUiSettings({ sidebar: serializeSidebarPreferencesForApi(normalized) });
      if (!silent) toast.success("Настройки меню сохранены");
    } catch {
      if (!silent) toast.info("Изменения меню сохранены на этом устройстве. Синхронизация с сервером сейчас недоступна.");
    } finally {
      setSidebarSaving(false);
    }
    return normalized;
  }, [isAuthenticated, sidebarStorageScope, toast]);

  const toggleSidebarCollapsed = React.useCallback(() => {
    const normalized = normalizeSidebarPreferences(sidebarPreferences);
    const nextMode = normalized.mode === "full" ? "compact" : "full";
    persistSidebarPreferences({
      ...normalized,
      mode: nextMode,
      collapsed: false,
    }, { silent: true });
  }, [persistSidebarPreferences, sidebarPreferences]);

  const openSidebarEditor = React.useCallback(() => {
    setSidebarDraft(normalizeSidebarPreferences(sidebarPreferences));
    setSidebarEditorOpen(true);
  }, [sidebarPreferences]);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const onToggle = () => toggleSidebarCollapsed();
    const onOpenEditor = () => openSidebarEditor();
    const onSetMode = (event) => {
      const mode = String(event?.detail?.mode || "").trim();
      if (!["full", "compact", "hidden"].includes(mode)) return;
      const normalized = normalizeSidebarPreferences(sidebarPreferences);
      persistSidebarPreferences({ ...normalized, mode, collapsed: false }, { silent: true });
    };

    window.addEventListener("burosoul:toggle-sidebar", onToggle);
    window.addEventListener("burosoul:open-sidebar-editor", onOpenEditor);
    window.addEventListener("burosoul:set-sidebar-mode", onSetMode);

    return () => {
      window.removeEventListener("burosoul:toggle-sidebar", onToggle);
      window.removeEventListener("burosoul:open-sidebar-editor", onOpenEditor);
      window.removeEventListener("burosoul:set-sidebar-mode", onSetMode);
    };
  }, [openSidebarEditor, persistSidebarPreferences, sidebarPreferences, toggleSidebarCollapsed]);

  const closeSidebarEditor = React.useCallback(() => {
    if (sidebarSaving) return;
    setSidebarEditorOpen(false);
    setSidebarDraft(normalizeSidebarPreferences(sidebarPreferences));
  }, [sidebarPreferences, sidebarSaving]);

  const resetSidebarEditor = React.useCallback(() => {
    setSidebarDraft({
      mode: "full",
      collapsed: false,
      density: "comfortable",
      showSectionLabels: true,
      showBadges: true,
      showSupport: true,
      showUserCard: true,
      hiddenIds: [],
      order: [],
      customItems: [],
      customSections: [],
      sectionAssignments: {},
    });
  }, []);

  const saveSidebarEditor = React.useCallback(async () => {
    await persistSidebarPreferences(sidebarDraft);
    setSidebarEditorOpen(false);
  }, [persistSidebarPreferences, sidebarDraft]);

  React.useEffect(() => {
    let isCancelled = false;

    if (!isAuthenticated) {
      setSubscriptionState(null);
      return () => {
        isCancelled = true;
      };
    }

    (async () => {
      try {
        const state = await subscriptionsApi.getMySubscription();
        if (isCancelled) return;
        setSubscriptionState(state && typeof state === "object" ? state : null);
      } catch {
        if (isCancelled) return;
        setSubscriptionState(null);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated, me?.id]);

  const closeMobileIfNeeded = React.useCallback(() => {
    if (typeof onCloseMobile === "function") onCloseMobile();
  }, [onCloseMobile]);

  const roleToken = normalizeRoleToken(role || roleLabel);
  const resolvedUserName = String(userName || me?.name || me?.fullName || me?.login || me?.email || "Гость").trim() || "Гость";
  const resolvedAvatarRaw = String(
    me?.avatarUrl || me?.avatar_url || me?.avatar || me?.photoUrl || me?.photo_url || me?.image || ""
  ).trim();
  const resolvedAvatarUrl = resolvedAvatarRaw ? resolveUrl(resolvedAvatarRaw) : "";
  const resolvedRoleLabel =
    !roleToken && resolvedUserName === "Гость"
      ? "Гость"
      : roleLabelRu(role || roleLabel, resolvedUserName);

  const hasRealSubscriptionState = hasMeaningfulSubscriptionState(subscriptionState);
  const statusFromState = hasRealSubscriptionState ? subscriptionState?.status : "";
  const trialFromState = hasRealSubscriptionState ? Boolean(subscriptionState?.trialActive) : false;
  const planFromState = hasRealSubscriptionState ? subscriptionState?.planName : "";

  const resolvedSubscriptionStatus = subscriptionStatusRu(
    meSubscriptionStatus(me) || statusFromState,
    meTrialFlag(me) || trialFromState
  );
  const resolvedPlanLabel = planLabelRu(mePlanLabel(me) || planFromState);
  const resolvedSubscriptionLabel = resolvedSubscriptionStatus || resolvedPlanLabel;

  const shouldShowSubscriptionBadge = roleToken !== "CLIENT" && Boolean(resolvedSubscriptionLabel);
  const resolvedUserStatus = shouldShowSubscriptionBadge
    ? `${resolvedSubscriptionLabel} · ${resolvedRoleLabel}`
    : resolvedRoleLabel;
  const resolvedNotificationsBadge =
    notificationsBadge == null ? unreadNotificationsCount : Number(notificationsBadge) || 0;
  const resolvedChatBadge = chatBadge == null ? Number(messageUnreadTotal) || 0 : Number(chatBadge) || 0;
  const resolvedCanOpenLiteratureEditor =
    canOpenLiteratureEditor == null ? PRIVILEGED_ROLES.has(roleToken) : Boolean(canOpenLiteratureEditor);

  const contextKey = detectContextKey(location.pathname);
  const platformItems = React.useMemo(
    () => buildPlatformItems({ roleToken, notificationsCount: resolvedNotificationsBadge, chatBadge: resolvedChatBadge }),
    [resolvedChatBadge, roleToken, resolvedNotificationsBadge]
  );
  const clientItems = React.useMemo(
    () => buildClientItems({ roleToken }),
    [roleToken]
  );
  const contentItems = React.useMemo(
    () => buildContentItems({ roleToken, isAuthenticated }),
    [isAuthenticated, roleToken]
  );
  const contextSection = React.useMemo(
    () =>
      buildContextSection({
        contextKey,
        roleToken,
        canOpenLiteratureEditor: resolvedCanOpenLiteratureEditor,
        isAuthenticated,
        location,
        chatBadge: resolvedChatBadge,
      }),
    [resolvedChatBadge, contextKey, isAuthenticated, location, roleToken, resolvedCanOpenLiteratureEditor]
  );
  const customItems = React.useMemo(
    () => buildCustomSidebarItems(normalizeSidebarPreferences(sidebarPreferences).customItems),
    [sidebarPreferences]
  );

  const customSectionOptions = React.useMemo(() => {
    const normalized = normalizeSidebarPreferences(sidebarPreferences);
    const base = defaultCustomSidebarSection();
    const seen = new Set([base.id]);
    const extra = (Array.isArray(normalized.customSections) ? normalized.customSections : [])
      .map((section, index) => normalizeCustomSidebarSection(section, index))
      .filter(Boolean)
      .filter((section) => {
        if (seen.has(section.id)) return false;
        seen.add(section.id);
        return true;
      });
    return [base, ...extra];
  }, [sidebarPreferences]);

  const customSectionLabelById = React.useMemo(
    () => new Map(customSectionOptions.map((section) => [section.id, section.label])),
    [customSectionOptions]
  );

  const visibleSections = React.useMemo(() => {
    const seen = new Set();
    const unique = (items = []) => dedupeNavItems(items).filter((item) => {
      const key = String(item?.to || item?.label || item?.navId || "");
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const normalized = normalizeSidebarPreferences(sidebarPreferences);
    const assignments = normalized.sectionAssignments || {};
    const customIds = new Set(customSectionOptions.map((section) => section.id));
    const customBuckets = new Map(customSectionOptions.map((section) => [section.id, []]));
    const pushAssigned = (item, fallbackSectionId = "") => {
      const assigned = assignments[item.navId] || fallbackSectionId;
      if (assigned && customIds.has(assigned)) {
        customBuckets.get(assigned).push(item);
        return true;
      }
      return false;
    };
    const keepSystem = (items = []) => items.filter((item) => !pushAssigned(item));

    const client = keepSystem(applySidebarPreferencesToItems(unique(clientItems), "client", normalized));
    const platform = keepSystem(applySidebarPreferencesToItems(unique(platformItems), "platform", normalized));
    const content = keepSystem(applySidebarPreferencesToItems(unique(contentItems), "content", normalized));
    const custom = applySidebarPreferencesToItems(unique(customItems), "custom", normalized);
    custom.forEach((item) => pushAssigned(item, item.sectionId || defaultCustomSidebarSection().id));
    const customGroups = customSectionOptions
      .map((section) => ({
        id: section.id,
        label: section.label,
        items: customBuckets.get(section.id) || [],
      }))
      .filter((group) => group.items.length > 0);
    const contextItems = applySidebarPreferencesToItems(unique(contextSection?.items || []), "context", {
      ...normalized,
      hiddenIds: [],
    });
    return { client, platform, content, custom, customGroups, contextItems };
  }, [clientItems, contentItems, contextSection?.items, customItems, customSectionOptions, platformItems, sidebarPreferences]);

  const editorItems = React.useMemo(() => {
    let menuIndex = 0;
    const normalized = normalizeSidebarPreferences(sidebarPreferences);
    const assignments = normalized.sectionAssignments || {};
    const customIds = new Set(customSectionOptions.map((section) => section.id));
    const applyAssignment = (item) => {
      const assigned = assignments[item.navId];
      if (assigned && customIds.has(assigned)) {
        return { ...item, editorSectionId: assigned, sectionLabel: customSectionLabelById.get(assigned) || item.sectionLabel };
      }
      return item;
    };
    const mapSection = (sectionKey, sectionItems, sectionLabel) =>
      dedupeNavItems(sectionItems).map((item) => applyAssignment(itemMetaForEditor(sectionKey, item, sectionLabel, menuIndex++)));

    const client = mapSection("client", clientItems, "Клиент");
    const platform = mapSection("platform", platformItems, "Платформа");
    const content = mapSection("content", contentItems, "Контент");
    const custom = customItems.map((item) => {
      const sectionId = item.sectionId || defaultCustomSidebarSection().id;
      return applyAssignment({
        ...itemMetaForEditor("custom", item, customSectionLabelById.get(sectionId) || "Моё меню", menuIndex++),
        navId: item.navId || item.key,
        locked: false,
        custom: true,
        editorSectionId: sectionId,
        sectionId,
      });
    });

    return [...client, ...platform, ...content, ...custom];
  }, [clientItems, contentItems, customItems, customSectionLabelById, customSectionOptions, platformItems, sidebarPreferences]);

  const handleLogout = React.useCallback(async () => {
    if (typeof onLogout === "function") {
      await onLogout();
      closeMobileIfNeeded();
      return;
    }

    try {
      await logout();
    } finally {
      closeMobileIfNeeded();
      navigate("/login", { replace: true });
    }
  }, [closeMobileIfNeeded, logout, navigate, onLogout]);

  const canShowLogout = Boolean(me?.id || roleToken || typeof onLogout === "function");
  const actionBusy = Boolean(actionMessageSending);
  const sidebarSettings = normalizeSidebarPreferences(sidebarPreferences);
  const sidebarMode = sidebarSettings.mode || "full";
  const sidebarCompact = sidebarMode === "compact";
  const sidebarHidden = sidebarMode === "hidden";
  const sidebarDensity = sidebarSettings.density || "comfortable";
  const settingsActive = location.pathname.startsWith("/dashboard/settings") || location.pathname.startsWith("/dashboard/diary");
  const financeActive = FINANCE_SECTION_ENABLED && location.pathname.startsWith("/dashboard/finance");
  const canShowFinance = FINANCE_SECTION_ENABLED && roleToken && roleToken !== "CLIENT";

  const openSupportModal = React.useCallback(
    (kind = "SUPPORT") => {
      if (!isAuthenticated) {
        toast.info("Войдите в аккаунт, чтобы написать в поддержку");
        navigate("/login", {
          state: {
            from: `${location.pathname || "/"}${location.search || ""}${location.hash || ""}`,
          },
        });
        return;
      }
      const category = String(kind || "").trim().toUpperCase() === "BUG" ? "BUG" : "SUPPORT";
      setSupportKind(category);
      setSupportDraft("");
      setSupportModalOpen(true);
    },
    [isAuthenticated, location.hash, location.pathname, location.search, navigate, toast]
  );

  const closeSupportModal = React.useCallback(() => {
    if (actionBusy) return;
    setSupportModalOpen(false);
    setSupportDraft("");
  }, [actionBusy]);

  const submitSupportMessage = React.useCallback(
    async (event) => {
      event.preventDefault();
      const body = String(supportDraft || "").trim();
      if (body.length < 6) {
        toast.info("Опишите ситуацию подробнее (минимум 6 символов)");
        return;
      }

      const route = `${location.pathname || "/"}${location.search || ""}${location.hash || ""}` || "/";
      setActionMessageSending(true);
      try {
        await chatApi.sendToActionDialog({ category: supportKind, content: body, pagePath: route });
        toast.success(supportKind === "BUG" ? "Сообщение об ошибке отправлено" : "Сообщение в поддержку отправлено");
        setSupportModalOpen(false);
        setSupportDraft("");
      } catch (error) {
        toast.error(error?.message || "Не удалось отправить сообщение");
      } finally {
        setActionMessageSending(false);
      }
    },
    [
      location.hash,
      location.pathname,
      location.search,
      supportDraft,
      supportKind,
      toast,
    ]
  );

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = isActiveItem(item, location, activeKey);
    const target = String(item?.to || "");
    const hasCreateLimit = Boolean(featureKeyForCreatePath(target));
    const label = String(item.label || "Раздел");

    if (typeof item.onSelect === "function" && !target) {
      return (
        <li key={item.key || item.label}>
          <button
            type="button"
            className={active ? "active" : ""}
            onClick={item.onSelect}
            aria-label={label}
            title={label}
            data-tour={`sidebar-${item.key || item.navId || "item"}`}
            data-sidebar-key={item.key || item.navId || "item"}
            data-sidebar-nav-id={item.navId || item.key || "item"}
          >
            <Icon />
            <span className="podcasts-sidebar__nav-text">{label}</span>
            {sidebarSettings.showBadges && Number(item.badge) > 0 ? <span className="podcasts-sidebar__badge">{item.badge}</span> : null}
          </button>
        </li>
      );
    }

    const handleClick = async (event) => {
      if (hasCreateLimit) {
        event.preventDefault();
        const allowed = await checkCreateAccess(target);
        if (!allowed) return;
        closeMobileIfNeeded();
        navigate(target);
        return;
      }
      closeMobileIfNeeded();
    };

    return (
      <li key={item.to || item.label}>
        <Link
          to={item.to}
          className={active ? "active" : ""}
          onClick={handleClick}
          aria-label={label}
          title={label}
          data-tour={`sidebar-${item.key || item.navId || "item"}`}
          data-sidebar-key={item.key || item.navId || "item"}
          data-sidebar-nav-id={item.navId || item.key || "item"}
        >
          <Icon />
          <span className="podcasts-sidebar__nav-text">{label}</span>
          {sidebarSettings.showBadges && Number(item.badge) > 0 ? <span className="podcasts-sidebar__badge">{item.badge}</span> : null}
        </Link>
      </li>
    );
  };

  return (
    <aside
      className={`podcasts-sidebar ${mobileOpen ? "is-open" : ""} ${sidebarHidden ? "is-hidden" : ""} ${sidebarCompact ? "is-compact" : ""} is-density-${sidebarDensity} ${!sidebarSettings.showSectionLabels ? "is-section-labels-hidden" : ""}`.trim()}
      data-sidebar-mode={sidebarMode}
    >
      <button
        type="button"
        className="podcasts-sidebar__restore"
        onClick={toggleSidebarCollapsed}
        aria-label="Развернуть боковое меню"
        title="Развернуть меню"
      >
        ☰
      </button>

      <div className="podcasts-sidebar__topbar">
        <Link className="podcasts-sidebar__logo" to="/" onClick={closeMobileIfNeeded} title="На главную">
          <img className="podcasts-sidebar__brand-logo" src={brandLogo} alt="Бюро Души" />
          <span className="podcasts-sidebar__brand-mini" aria-hidden="true">БД</span>
        </Link>

        <span className="podcasts-sidebar__topbar-spacer" aria-hidden="true" />
      </div>

      {visibleSections.client.length ? (
        <div className="podcasts-sidebar__section">
          <div className="podcasts-sidebar__label">Клиент</div>
          <ul className="podcasts-sidebar__nav">{visibleSections.client.map(renderNavItem)}</ul>
        </div>
      ) : null}

      <div className="podcasts-sidebar__section">
        <div className="podcasts-sidebar__label">Платформа</div>
        <ul className="podcasts-sidebar__nav">{visibleSections.platform.map(renderNavItem)}</ul>
      </div>

      <div className="podcasts-sidebar__section">
        <div className="podcasts-sidebar__label">Контент</div>
        <ul className="podcasts-sidebar__nav">{visibleSections.content.map(renderNavItem)}</ul>
      </div>

      {visibleSections.customGroups.map((group) => (
        <div className="podcasts-sidebar__section" key={group.id}>
          <div className="podcasts-sidebar__label">{group.label}</div>
          <ul className="podcasts-sidebar__nav">{group.items.map(renderNavItem)}</ul>
        </div>
      ))}

      {visibleSections.contextItems.length ? (
        <div className="podcasts-sidebar__section">
          <div className="podcasts-sidebar__label">{contextSection.label}</div>
          <ul className="podcasts-sidebar__nav">{visibleSections.contextItems.map(renderNavItem)}</ul>
        </div>
      ) : null}

      {extraContent ? <div className="podcasts-sidebar__section podcasts-sidebar__section--custom">{extraContent}</div> : null}

      <div className="podcasts-sidebar__footer">
        <div className="podcasts-sidebar__utility">
          {canShowFinance ? (
            <Link
              to="/dashboard/finance"
              className={`podcasts-sidebar__utility-btn ${financeActive ? "is-active" : ""}`.trim()}
              onClick={closeMobileIfNeeded}
              aria-label="Финансы"
              title="Финансы"
            >
              <IconFinance />
              <span className="podcasts-sidebar__utility-text">Финансы</span>
            </Link>
          ) : null}

          <Link
            to="/dashboard/settings"
            className={`podcasts-sidebar__utility-btn ${settingsActive ? "is-active" : ""}`.trim()}
            onClick={closeMobileIfNeeded}
            aria-label="Настройки"
            title="Настройки"
          >
            <IconSettings />
            <span className="podcasts-sidebar__utility-text">Настройки</span>
          </Link>

          {sidebarSettings.showSupport ? (
            <button
              type="button"
              className="podcasts-sidebar__utility-btn"
              onClick={() => openSupportModal("SUPPORT")}
              disabled={actionBusy}
              aria-label="Поддержка"
              title="Поддержка"
            >
              <IconSupport />
              <span className="podcasts-sidebar__utility-text">Поддержка</span>
            </button>
          ) : null}
        </div>

        {sidebarSettings.showUserCard ? (
          <div className="podcasts-sidebar__user">
            <div className="podcasts-sidebar__user-avatar">
              <span className="podcasts-sidebar__user-avatar-fallback">{initials(resolvedUserName)}</span>
              {resolvedAvatarUrl ? (
                <img
                  src={resolvedAvatarUrl}
                  alt={resolvedUserName}
                  loading="lazy"
                  decoding="async"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              ) : null}
            </div>
            <div className="podcasts-sidebar__user-meta">
              <div className="podcasts-sidebar__user-name">{resolvedUserName}</div>
              <div className="podcasts-sidebar__user-role">{resolvedUserStatus}</div>
            </div>
            {canShowLogout ? (
              <button type="button" className="podcasts-sidebar__logout" onClick={handleLogout} title="Выйти">
                ⎋
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      <SidebarEditorModal
        open={sidebarEditorOpen}
        draft={sidebarDraft}
        setDraft={setSidebarDraft}
        items={editorItems}
        onClose={closeSidebarEditor}
        onSave={saveSidebarEditor}
        onReset={resetSidebarEditor}
        saving={sidebarSaving}
      />

      <Modal
        open={supportModalOpen}
        onClose={closeSupportModal}
        title="Обращение в поддержку"
        size="md"
        className="support-modal-shell"
        cardClassName="support-modal-card"
        actions={(
          <>
            <button type="button" className="support-modal-btn support-modal-btn--ghost" onClick={closeSupportModal} disabled={actionBusy}>
              Отмена
            </button>
            <button type="submit" form="supportRequestForm" className="support-modal-btn support-modal-btn--primary" disabled={actionBusy}>
              {actionBusy ? "Отправляем…" : "Отправить"}
            </button>
          </>
        )}
      >
        <div className="support-modal-note">Сообщение получит служба поддержки</div>
        <div className="support-modal-kind" role="tablist" aria-label="Тип обращения">
          <button
            type="button"
            className={`support-modal-kind__btn ${supportKind === "SUPPORT" ? "is-active" : ""}`}
            onClick={() => setSupportKind("SUPPORT")}
            disabled={actionBusy}
          >
            Вопрос
          </button>
          <button
            type="button"
            className={`support-modal-kind__btn ${supportKind === "BUG" ? "is-active" : ""}`}
            onClick={() => setSupportKind("BUG")}
            disabled={actionBusy}
          >
            Ошибка
          </button>
        </div>
        <form id="supportRequestForm" className="support-modal-form" onSubmit={submitSupportMessage}>
          <label className="b-field support-modal-field">
            <span className="b-field__label">{supportKind === "BUG" ? "Что именно сломалось?" : "Что нужно сделать?"}</span>
            <textarea
              className="b-input support-modal-textarea"
              value={supportDraft}
              onChange={(event) => setSupportDraft(event.target.value)}
              placeholder={
                supportKind === "BUG"
                  ? "Что делали, что ожидали увидеть, и что произошло фактически"
                  : "Кратко опишите вопрос, чтобы мы быстрее помогли"
              }
              rows={6}
              maxLength={3000}
              autoFocus
              disabled={actionBusy}
            />
          </label>
          <div className="support-modal-counter">{supportDraft.trim().length} / 3000</div>
        </form>
      </Modal>
      {limitModal}
      <GlobalOnboardingTourBridge />
    </aside>
  );
}
